import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';

interface SEOProps {
  /** Page title - will be formatted as "{title} | {siteName}" */
  title?: string;
  /** Meta description for the page */
  description?: string;
  /** Canonical URL for this page (overrides auto-generated) */
  canonical?: string;
  /** Open Graph image URL */
  image?: string;
  /** Page type for Open Graph (default: "website") */
  type?: 'website' | 'article' | 'product';
  /** Don't append site name to title */
  titleOnly?: boolean;
}

/**
 * SEO component for managing page meta tags.
 *
 * Usage:
 * ```tsx
 * <SEO
 *   title="Weekly Ads"
 *   description="View our latest weekly ads and specials"
 * />
 * ```
 *
 * This will render:
 * - <title>Weekly Ads | {Site Name}</title>
 * - <meta name="description" content="View our latest weekly ads and specials" />
 * - Open Graph and Twitter card tags
 */
export const SEO = ({
  title,
  description,
  canonical,
  image,
  type = 'website',
  titleOnly = false,
}: SEOProps) => {
  const location = useLocation();
  const { data: siteInfo } = useSiteInfo();

  const siteName = siteInfo?.SiteTitle || 'Store';
  const siteTagLine = siteInfo?.SiteTagLine || '';
  const siteLogo = siteInfo?.SiteLogo?.url || '';
  const primaryColor = siteInfo?.SitePrimaryColor || '#1e40af';

  // Format the full title
  const fullTitle = title
    ? (titleOnly ? title : `${title} | ${siteName}`)
    : (siteTagLine ? `${siteName} | ${siteTagLine}` : siteName);

  // Use provided description or fall back to site tagline
  const metaDescription = description || siteTagLine || `Welcome to ${siteName}`;

  // Use provided image or fall back to site logo
  const ogImage = image || siteLogo;

  // Generate canonical URL from current location
  const currentUrl = canonical || `${window.location.origin}${location.pathname}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />

      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* Theme Color */}
      <meta name="theme-color" content={primaryColor} />

      {/* Apple Mobile Web App */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content={siteName} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </Helmet>
  );
};

export default SEO;
